import { client } from './claims';

// Pre-defined void reasons
export const VOID_REASONS = [
  'Duplicate payment',
  'Client request',
  'Client originally cancelled in cooling off period',
  'Complaint',
  'Fraud Alleged',
  'Payment Plan Agreed',
  'Financial/Bankrupt/Insolvent',
  'Health/Capacity',
  'No/Invalid Signature',
  'Over 70',
  'Error',
  'Other'
] as const;

export type VoidReason = typeof VOID_REASONS[number];

export interface StatusHistoryEntry {
  status: string;
  timestamp: string;
  actor: string;
  reason?: string;
}

export interface CheckHistoryEntry {
  checkedAt: string;
  checkedBy: string;
  notes?: string;
}

export interface WorldpayConfirmation {
  confirmedBy?: string;
  confirmedAt?: string;
  verifiedAt?: string;
  verifiedEventId?: string;
  status: 'staff_confirmed' | 'verified' | 'webhook_only';
}

// Final fix-wave re-review, Blocker 1: written by
// cancellation_payment_service.py's `_flag_terminal_discrepancy` when a
// webhook reports an amount that neither matches the ledger nor can be
// safely settled/adjusted against an already-terminal (completed/voided)
// record -- e.g. staff confirmed a FINAL payment at one figure and
// Worldpay's own webhook later reports a different one (the £2160-for-£216
// keying slip). `recordedVia`/`reportedVia` name which channel ('staff' |
// 'webhook' | 'unknown') produced each figure.
export interface WorldpayAmountDiscrepancy {
  recordedAmount: number;
  recordedVia: string;
  reportedAmount: number;
  reportedVia: string;
  transactionId?: string | null;
  flaggedAt: string;
  adjusted: boolean;
  refusedReason?: string;
  severe: boolean;
}

// Per-payment audit trail written by `append_worldpay_event_log` for a
// non-success Worldpay event (e.g. a declined card) matched to THIS
// record -- distinct from the global unmatched-events queue
// (fetchUnmatchedWorldpayEvents), which covers events across ALL payments.
export interface WorldpayEventLogEntry {
  eventId: string;
  eventType?: string;
  amount: number;
  receivedAt: string;
}

export interface CancellationPayment {
  id: string;
  clientId: string;
  clientEmail: string;
  clientName?: string;
  claimIds: string[];
  amount: number;
  baseAmount: number;
  vatAmount: number;
  paymentLinkUrl: string;
  stripeSessionId?: string;
  status: string;
  createdAt: string;
  paidAt?: string;
  expiresAt?: string;
  paymentType?: string;
  isManual?: boolean;
  metadata?: Record<string, unknown>;
  reinstatedAt?: string;
  reinstatedClaimIds?: string[];
  // Void tracking fields
  voidedAt?: string;
  voidedBy?: string;
  voidReason?: string;
  previousStatus?: string;
  statusHistory?: StatusHistoryEntry[];
  // Audit/Check tracking fields
  isChecked?: boolean;
  checkedAt?: string;
  checkedBy?: string;
  checkNotes?: string;
  checkHistory?: CheckHistoryEntry[];
  // Chaser tracking fields
  chaserSentAt?: string;
  invoiceNumber?: string;
  invoiceDocUrl?: string;
  invoiceBlobName?: string;
  // Manual payment tracking fields
  manualPayment?: {
    amountPaid: number;
    expectedAmount: number;
    outstandingBalance: number;
    isFinal: boolean;
    recordedBy: string;
    recordedAt: string;
    notes?: string;
  };
  paymentMethod?: string;
  // Cancellation reason (enriched from claims)
  cancellationReason?: string;
  cancellationSource?: string;
  // Worldpay phone-payment tracking fields
  worldpayReference?: string;
  worldpayTransactionId?: string;
  worldpayConfirmation?: WorldpayConfirmation;
  // Final fix-wave C2 reconciliation-flag fields -- set when a settlement
  // was recorded but deliberately NOT auto-completed (an overpayment on any
  // channel, or an ambiguous Worldpay ledger match). Surfaced via the
  // needs-reconciliation queue.
  worldpayReconciliationFlag?: boolean;
  reconciliationReason?: string;
  reconciliationDetectedAt?: string;
  // Terminal keying-slip discrepancy (final fix-wave re-review Blocker 1) --
  // present only on the specific flagged-record case where a webhook amount
  // couldn't be matched or safely adjusted against an already-terminal
  // record. May coexist with the fields above (both set
  // worldpayReconciliationFlag).
  worldpayAmountDiscrepancy?: WorldpayAmountDiscrepancy;
  // This payment's own declined/failed Worldpay attempts (see
  // WorldpayEventLogEntry) -- separate from the global worldpayEvents
  // container read via fetchUnmatchedWorldpayEvents.
  worldpayEvents?: WorldpayEventLogEntry[];
}

export interface CancellationPaymentListResponse {
  payments: CancellationPayment[];
  count: number;
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

  export interface PaymentStats {
    total: number;
    pending: number;
    paid: number;
    voided: number;
    owed: number;
    paidAmount: number;
  }

export interface PaymentQueryParams {
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export const fetchCancellationPayments = async (
  params: PaymentQueryParams = {}
): Promise<CancellationPaymentListResponse> => {
  const response = await client.get<CancellationPaymentListResponse>(
    '/ui/admin/cancellation-payments',
    { params }
  );
  return response.data;
};

  export const fetchPaymentStats = async (): Promise<PaymentStats> => {
    const response = await client.get<PaymentStats>(
      '/ui/admin/cancellation-payments/stats'
    );
    return response.data;
  };

export interface ManualPaymentRequest {
  claimIds: string[];
  amount: number;
  description?: string;
}

export interface ManualPaymentResponse {
  paymentId: string;
  paymentLinkUrl: string;
  amount: number;
  baseAmount: number;
  vatAmount: number;
  expiresAt?: string;
  claimId: string;
  clientEmail: string;
  clientName?: string;
  status: string;
}

export const createManualCancellationPayment = async (
  payload: ManualPaymentRequest
): Promise<ManualPaymentResponse> => {
  const response = await client.post<ManualPaymentResponse>(
    '/ui/admin/cancellation-payments/manual',
    payload
  );
  return response.data;
};

export interface VoidPaymentRequest {
  voidReason: VoidReason;
  updateClaimStatus?: boolean;
}

export interface VoidPaymentResponse {
  success: boolean;
  paymentId: string;
  previousStatus?: string;
  voidReason?: string;
  voidedBy?: string;
  message?: string;
  updateClaimStatus?: boolean;
}

export const voidCancellationPayment = async (
  paymentId: string,
  request: VoidPaymentRequest
): Promise<VoidPaymentResponse> => {
  const response = await client.post<VoidPaymentResponse>(
    `/ui/admin/cancellation-payments/${encodeURIComponent(paymentId)}/void`,
    request
  );
  return response.data;
};

export interface CheckPaymentRequest {
  notes?: string;
}

export interface CheckPaymentResponse {
  success: boolean;
  paymentId: string;
  checkedAt: string;
  checkedBy: string;
  notes?: string;
  message?: string;
}

export const checkCancellationPayment = async (
  paymentId: string,
  request?: CheckPaymentRequest
): Promise<CheckPaymentResponse> => {
  const response = await client.post<CheckPaymentResponse>(
    `/ui/admin/cancellation-payments/${encodeURIComponent(paymentId)}/check`,
    request || {}
  );
  return response.data;
};

export interface MarkManualPaymentRequest {
  amountPaid: number;
  isFinal: boolean;
  notes?: string;
}

export interface MarkManualPaymentResponse {
  success: boolean;
  paymentId: string;
  status: string;
  amountPaid: number;
  outstandingBalance: number;
  message?: string;
  // Final fix-wave C2: set when this settlement's total would exceed the
  // record's expected amount -- the money is recorded but NOT auto-
  // completed, and needs a human to resolve it via the needs-reconciliation
  // queue. Distinct from a plain success and must be surfaced as such.
  reconciliationFlag?: boolean;
}

export const markManualPayment = async (
  paymentId: string,
  request: MarkManualPaymentRequest
): Promise<MarkManualPaymentResponse> => {
  const response = await client.post<MarkManualPaymentResponse>(
    `/ui/admin/cancellation-payments/${encodeURIComponent(paymentId)}/mark-manual-payment`,
    request
  );
  return response.data;
};

export interface WorldpayReferenceResponse {
  worldpayReference: string;
  virtualTerminalUrl: string;
}

export const fetchOrCreateWorldpayReference = async (
  paymentId: string
): Promise<WorldpayReferenceResponse> => {
  const response = await client.post<WorldpayReferenceResponse>(
    `/ui/admin/cancellation-payments/${encodeURIComponent(paymentId)}/worldpay-reference`
  );
  return response.data;
};

export interface ConfirmWorldpayPaymentRequest {
  amountPaid: number;
  isFinal: boolean;
  worldpayReference: string;
  worldpayTransactionReference?: string;
  notes?: string;
}

export interface ConfirmWorldpayPaymentResponse {
  success: boolean;
  paymentId: string;
  status: string;
  amountPaid: number;
  outstandingBalance: number;
  worldpayConfirmation: WorldpayConfirmation;
  message?: string;
  // Final fix-wave C2: set when this settlement's total would exceed the
  // record's expected amount -- the money is recorded but NOT auto-
  // completed, and needs a human to resolve it via the needs-reconciliation
  // queue. Distinct from a plain success and must be surfaced as such.
  reconciliationFlag?: boolean;
}

export const confirmWorldpayPayment = async (
  paymentId: string,
  request: ConfirmWorldpayPaymentRequest
): Promise<ConfirmWorldpayPaymentResponse> => {
  const response = await client.post<ConfirmWorldpayPaymentResponse>(
    `/ui/admin/cancellation-payments/${encodeURIComponent(paymentId)}/worldpay-confirm`,
    request
  );
  return response.data;
};

export interface UnmatchedWorldpayEvent {
  id: string;
  // Worldpay rows always carry transactionReference; Stripe rows (Task 2:
  // this same queue also surfaces Stripe's unmatched/anomaly_flagged
  // events, tagged by `provider`) carry paymentId instead -- never both.
  transactionReference?: string;
  paymentId?: string;
  // Stripe-only locator (fix round 1, IMPORTANT 2): paymentId is None by
  // construction on an 'unmatched' Stripe row, so sessionId/customerEmail
  // are the only things a human can actually search Stripe FOR.
  sessionId?: string;
  customerEmail?: string;
  // The human-facing explanation persisted onto the row itself, not just
  // returned in the webhook's own (unread) HTTP response.
  message?: string;
  amount?: number | null;
  eventType: string;
  receivedAt: string;
  // Which webhook produced this row -- absent on older cached data,
  // defaults to 'worldpay' at the point of use since this queue was
  // Worldpay-only before Task 2.
  provider?: 'worldpay' | 'stripe' | string;
  // Why this needs finance's attention: no matching payment record found
  // ('unmatched'), matched but the settlement attempt itself failed
  // ('settlement_failed'), a Worldpay provider settlement failure that
  // requires review ('provider_settlement_failed'), settled on a guessed same-amount match rather
  // than a confirmed transaction identity ('flagged_for_reconciliation'), a
  // card was declined/refused after staff clicked "payment taken"
  // ('non_success' matched a payment, 'unmatched_non_success' did not --
  // final fix-wave declined-card visibility fix), or a Stripe payment that
  // resolved onto a record in an anomalous state ('anomaly_flagged').
  status?:
    | 'unmatched'
    | 'settlement_failed'
    | 'provider_settlement_failed'
    | 'flagged_for_reconciliation'
    | 'non_success'
    | 'unmatched_non_success'
    | 'anomaly_flagged'
    | string;
}

// Final fix-wave (Important 2): `stripeQueueError` distinguishes "Stripe's
// half of this queue genuinely has nothing to report" from "Stripe's half
// is broken" -- in production, where Worldpay ships disabled, Stripe is
// the ONLY feed into this queue, so a swallowed fetch failure used to look
// identical to a clean empty list (see admin.py's
// list_unmatched_worldpay_events_route). Events from the healthy provider
// are still returned in `events` alongside the provider-specific flag.
export interface UnmatchedWorldpayEventsResponse {
  events: UnmatchedWorldpayEvent[];
  worldpayEnabled?: boolean;
  worldpayQueueError?: boolean;
  stripeQueueError?: boolean;
}

export const fetchUnmatchedWorldpayEvents = async (): Promise<UnmatchedWorldpayEventsResponse> => {
  const response = await client.get<UnmatchedWorldpayEventsResponse>(
    '/ui/admin/cancellation-payments/worldpay-unmatched'
  );
  return response.data;
};

// Final fix-wave C2: payments flagged for reconciliation -- an overpayment
// on any settlement channel, or an ambiguous Worldpay ledger match -- money
// is already recorded on these but deliberately NOT auto-completed. Returns
// full payment records (same shape as the main list), per the backend
// report's documented response shape `{ payments: [...] }`.
export const fetchPaymentsNeedingReconciliation = async (): Promise<CancellationPayment[]> => {
  const response = await client.get<{ payments: CancellationPayment[] }>(
    '/ui/admin/cancellation-payments/needs-reconciliation'
  );
  return response.data.payments;
};

// The other half of the needs-reconciliation queue (the bug fix this branch
// adds): clears worldpayReconciliationFlag once a human has reviewed the
// entry and confirmed it's fine. Metadata-only -- never touches money,
// status, or ledger entries. `notes` is required by the backend (400 if
// missing/blank); the actor is derived server-side from the authenticated
// session, never sent from here.
export interface ResolveWorldpayReconciliationRequest {
  notes: string;
  expectedDetectedAt: string;
}

export interface ResolveWorldpayReconciliationResponse {
  paymentId: string;
  worldpayReconciliationFlag: boolean;
  resolvedBy?: string;
  resolvedAt?: string;
  alreadyResolved?: boolean;
}

export const resolveWorldpayReconciliationFlag = async (
  paymentId: string,
  request: ResolveWorldpayReconciliationRequest
): Promise<ResolveWorldpayReconciliationResponse> => {
  const response = await client.post<ResolveWorldpayReconciliationResponse>(
    `/ui/admin/cancellation-payments/${encodeURIComponent(paymentId)}/resolve-reconciliation`,
    request
  );
  return response.data;
};

// Side-effect-free reachability probe for the Worldpay phone-payment
// feature. There is no dedicated endpoint that exposes WORLDPAY_ENABLED to
// the UI, so this reuses the existing read-only unmatched-events endpoint
// (deployed in the same admin.py blueprint as worldpay-reference/
// worldpay-confirm, and requiring no payment id, unlike those two POSTs).
// Fix round 1 (CRITICAL): the worldpay-unmatched endpoint used to 503
// whenever WORLDPAY_ENABLED was off, so a plain try/catch on this call was
// enough to tell Worldpay-specific UI apart. It now returns 200 regardless
// (Stripe's own unmatched events must stay visible even when Worldpay is
// disabled), so the flag is read from the body instead of inferred from
// whether the request merely succeeded.
export const checkWorldpayAvailable = async (): Promise<boolean> => {
  try {
    const response = await client.get<{ worldpayEnabled?: boolean }>(
      '/ui/admin/cancellation-payments/worldpay-unmatched'
    );
    return response.data.worldpayEnabled === true;
  } catch {
    return false;
  }
};

export interface ResendPaymentResponse {
  success: boolean;
  message?: string;
  paymentLinkUrl?: string;
  expiryDate?: string;
}

export const resendPaymentLink = async (
  paymentId: string
): Promise<ResendPaymentResponse> => {
  const response = await client.post<ResendPaymentResponse>(
    `/ui/claims/payments/${encodeURIComponent(paymentId)}/resend`
  );
  return response.data;
};

// Simple Chaser Types and API functions
export interface SimpleChaserStats {
  lastRunAt?: string | null;
  totalChased: number;
  lastRunProcessed?: number;
  lastRunFailed?: number;
  lastRunBy?: string | null;
  /** Whether the signed-in user may run chasers (named staff only). */
  canRun?: boolean;
  /** Who may run chasers, in plain words, built by the backend from its setting. */
  chaserRunnersLabel?: string;
}

export interface SimpleChaserResult {
  processed: number;
  succeeded: number;
  failed: number;
  message: string;
  error?: string;
}

export const fetchSimpleChaserStats = async (): Promise<SimpleChaserStats> => {
  const response = await client.get<SimpleChaserStats>(
    '/ui/admin/cancellation-payments/simple-chaser-stats'
  );
  return response.data;
};

export const runSimpleChasers = async (
  limit: number = 50
): Promise<SimpleChaserResult> => {
  const response = await client.post<SimpleChaserResult>(
    '/ui/admin/cancellation-payments/run-simple-chasers',
    { limit }
  );
  return response.data;
};
